<template>
  <div class="manager-company-records-pane" :class="{ 'manager-company-records-pane--timesheets': isManagerTimesheetsView }">
    <header class="manager-company-records-header">
      <div class="manager-company-records-header-row">
        <button type="button" class="manager-company-records-back" @click="goBack">
          <i class="bi bi-arrow-left"></i> Back to client
        </button>
        <div class="manager-company-records-heading">
          <div class="manager-company-records-heading-main">
            <h1 v-if="!isManagerTimesheetsView">{{ pageTitle }}</h1>
            <p
              v-if="companyName"
              class="manager-company-records-sub"
              :class="{ 'manager-company-records-sub--emphasized': isManagerTimesheetsView }"
            >
              {{ companyName }}
            </p>
          </div>
        </div>
      </div>
    </header>

    <div
      class="manager-company-records-subnav-bar"
      :class="{ 'manager-company-records-subnav-bar--timesheets': isManagerTimesheetsView }"
    >
      <nav class="manager-company-records-subnav" aria-label="Company records">
        <router-link
          v-for="item in managerCompanyNavLinks"
          :key="item.routeName"
          class="manager-company-records-subnav-link"
          :class="{ 'manager-company-records-subnav-link--active': routeName === item.routeName }"
          :to="managerRecordsRoute(item.routeName)"
        >
          {{ item.label }}
        </router-link>
      </nav>
      <div
        v-if="isManagerTimesheetsView"
        class="manager-ts-list-toggle"
        role="group"
        aria-label="Timesheet list type"
      >
        <button
          type="button"
          class="manager-ts-list-toggle__btn"
          :class="{ 'manager-ts-list-toggle__btn--active': tsListSource === 'consultant' }"
          @click="setTimesheetListSource('consultant')"
        >
          Consultant timesheet
        </button>
        <button
          type="button"
          class="manager-ts-list-toggle__btn"
          :class="{ 'manager-ts-list-toggle__btn--active': tsListSource === 'hr' }"
          @click="setTimesheetListSource('hr')"
        >
          HR timesheet
        </button>
      </div>
    </div>

    <template v-if="!isManagerTimesheetsView">
      <div
        v-if="isManagerRecordsListView"
        class="manager-records-filters manager-ts-filters"
        :aria-label="isManagerInvoicesView ? 'Invoice filters' : 'Bill filters'"
      >
        <div class="manager-ts-filters-row">
          <div class="manager-ts-filter-group manager-records-filter-group--search">
            <label :for="recordsSearchInputId" class="manager-ts-filter-label-text">
              {{ isManagerInvoicesView ? 'Invoice #' : 'Bill #' }}
            </label>
            <input
              :id="recordsSearchInputId"
              v-model="recordsSearch"
              type="search"
              class="manager-records-search-input"
              :placeholder="isManagerInvoicesView ? 'Search invoice number…' : 'Search bill number…'"
              autocomplete="off"
            />
          </div>
          <div v-if="isManagerInvoicesView" class="manager-ts-filter-group">
            <label for="manager-records-customer" class="manager-ts-filter-label-text">Customer</label>
            <select
              id="manager-records-customer"
              v-model="recordsCustomerId"
              class="manager-ts-select manager-records-select"
            >
              <option value="">All customers</option>
              <option v-for="c in recordFilterCustomers" :key="c.id" :value="String(c.id)">
                {{ c.customerName || c.customerId || `Customer ${c.id}` }}
              </option>
            </select>
          </div>
          <div v-if="isManagerBillsView" class="manager-ts-filter-group">
            <label for="manager-records-vendor" class="manager-ts-filter-label-text">Vendor</label>
            <select
              id="manager-records-vendor"
              v-model="recordsVendorId"
              class="manager-ts-select manager-records-select"
            >
              <option value="">All vendors</option>
              <option v-for="v in recordFilterVendors" :key="v.id" :value="String(v.id)">
                {{ v.vendorName || v.vendorId || `Vendor ${v.id}` }}
              </option>
            </select>
          </div>
          <div class="manager-ts-filter-group">
            <label for="manager-records-status" class="manager-ts-filter-label-text">Status</label>
            <select id="manager-records-status" v-model="recordsStatus" class="manager-ts-select manager-records-select">
              <option value="">All status</option>
              <option v-for="opt in recordsStatusFilterOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
          <div class="manager-ts-filter-group">
            <label for="manager-records-period" class="manager-ts-filter-label-text">Date range</label>
            <select id="manager-records-period" v-model="recordsDatePreset" class="manager-ts-select manager-records-select">
              <option value="all">All</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="this_quarter">This quarter</option>
              <option value="this_year">This year</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div class="manager-ts-filter-group">
            <span class="manager-ts-filter-label-text">From date</span>
            <div class="manager-ts-datepicker-wrap">
              <DatePicker
                v-model="recordsFromDateUI"
                placeholder="MM/DD/YYYY"
                :readonly="recordsDatePreset !== 'custom'"
              />
            </div>
          </div>
          <div class="manager-ts-filter-group">
            <span class="manager-ts-filter-label-text">To date</span>
            <div class="manager-ts-datepicker-wrap">
              <DatePicker
                v-model="recordsToDateUI"
                placeholder="MM/DD/YYYY"
                :readonly="recordsDatePreset !== 'custom'"
              />
            </div>
          </div>
          <div class="manager-ts-filter-group manager-records-filter-actions">
            <button type="button" class="manager-records-clear-btn" @click="clearRecordsFilters">
              Clear filters
            </button>
          </div>
        </div>
        <p v-if="!isRecordsDateRangeValid" class="manager-ts-filter-hint manager-ts-filter-hint--error">
          From date cannot be after To date
        </p>
      </div>

      <p v-if="loadError" class="manager-company-records-error">{{ loadError }}</p>
      <div v-if="loading" class="manager-company-records-loading">Loading…</div>
      <div v-else class="manager-company-records-table-wrap">
        <p v-if="isManagerRecordsListView && recordsTotal > 0" class="manager-records-list-meta">
          {{ recordsTotal.toLocaleString() }} {{ recordsListLabel }}
        </p>
        <table class="manager-company-records-table">
          <thead>
            <tr>
              <th v-for="col in tableColumns" :key="col.key">{{ col.label }}</th>
              <th class="manager-company-records-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="rowKey(row)">
              <td v-for="col in tableColumns" :key="col.key">{{ cellValue(row, col) }}</td>
              <td>
                <button type="button" class="manager-company-records-edit-btn" @click="openEdit(row)">
                  Edit
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!rows.length" class="manager-company-records-empty">No records found.</p>
        <div
          v-if="isManagerRecordsListView && recordsTotal > 0"
          class="manager-records-pagination-wrap hr-timesheets-pagination-wrapper"
        >
          <PaginationComponent
            :current-page="recordsCurrentPage"
            :total-items="recordsTotal"
            :items-per-page="recordsItemsPerPage"
            :item-label="recordsListLabel"
            :show-items-per-page="true"
            @page-change="handleRecordsPageChange"
            @items-per-page-change="handleRecordsItemsPerPageChange"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <p v-if="loadError" class="manager-company-records-error">{{ loadError }}</p>
      <p v-if="timesheetCardAttachmentError" class="manager-company-records-error manager-ts-card-attachment-banner">
        {{ timesheetCardAttachmentError }}
      </p>
      <div class="hrTimesheets-container manager-ts-hr-layout">
        <div class="hrTimesheets-filters-section" aria-label="Timesheet filters">
          <div class="hrTimesheets-filters-row">
            <div class="hrTimesheets-filter-group">
              <label for="manager-ts-consultant-filter" class="hrTimesheets-required-label">Consultant</label>
              <select
                id="manager-ts-consultant-filter"
                v-model="tsFilterConsultantPk"
                class="hrTimesheets-customer-select manager-ts-hr-select manager-ts-hr-select--consultant"
              >
                <option value="">All consultants</option>
                <option v-for="opt in managerTsConsultantSelectOptions" :key="opt.pk" :value="String(opt.pk)">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="hrTimesheets-filter-group">
              <label for="manager-ts-period" class="hrTimesheets-required-label">Date range</label>
              <select id="manager-ts-period" v-model="tsDatePreset" class="hrTimesheets-customer-select manager-ts-hr-select">
                <option value="all">All</option>
                <option value="this_week">This week</option>
                <option value="last_week">Last week</option>
                <option value="this_month">This month</option>
                <option value="last_month">Last month</option>
                <option value="this_quarter">This financial quarter</option>
                <option value="this_year">This financial year</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="hrTimesheets-filter-group">
              <span class="hrTimesheets-required-label">From date</span>
              <div class="hrTimesheets-date-input-wrapper">
                <DatePicker
                  v-model="tsFromDateUI"
                  placeholder="MM/DD/YYYY"
                  :readonly="tsDatePreset !== 'custom'"
                />
              </div>
              <span v-if="!isTimesheetDateRangeValid" class="hrTimesheets-error-text">
                From date cannot be after To date
              </span>
            </div>
            <div class="hrTimesheets-filter-group">
              <span class="hrTimesheets-required-label">To date</span>
              <div class="hrTimesheets-date-input-wrapper">
                <DatePicker
                  v-model="tsToDateUI"
                  placeholder="MM/DD/YYYY"
                  :readonly="tsDatePreset !== 'custom'"
                />
              </div>
            </div>
            <div class="hrTimesheets-filter-group">
              <label for="manager-ts-status" class="hrTimesheets-required-label">Status</label>
              <select id="manager-ts-status" v-model="tsStatus" class="hrTimesheets-customer-select manager-ts-hr-select">
                <option value="">All status</option>
                <template v-if="tsListSource === 'consultant'">
                  <option value="Time Sheet Created">Time Sheet Created</option>
                  <option value="Invoice Done">Invoice Done</option>
                  <option value="Payment Received">Payment Received</option>
                  <option value="Partial Payment Received">Partial Payment Received</option>
                </template>
                <template v-else>
                  <option value="Submitted">Submitted</option>
                  <option value="Approved">Approved</option>
                  <option value="Processed">Processed</option>
                </template>
              </select>
            </div>
            <div v-if="tsListSource === 'consultant'" class="hrTimesheets-filter-group">
              <label for="manager-ts-bill" class="hrTimesheets-required-label">Bill status</label>
              <select id="manager-ts-bill" v-model="tsBillStatus" class="hrTimesheets-customer-select manager-ts-hr-select">
                <option value="">All</option>
                <option value="Not Applicable">Not Applicable</option>
                <option value="Bill Created">Bill Created</option>
                <option value="Bill Paid">Bill Paid</option>
              </select>
            </div>
            <div v-else class="hrTimesheets-filter-group">
              <span class="hrTimesheets-required-label">Bill status</span>
              <select
                class="hrTimesheets-customer-select manager-ts-hr-select"
                disabled
                aria-disabled="true"
                title="HR timesheets do not use bill status"
              >
                <option>Not applicable</option>
              </select>
            </div>
          </div>
          <div v-if="showManagerTsPartyFilters" class="hrTimesheets-filters-row manager-ts-consultant-filters-row">
            <div class="hrTimesheets-filter-group">
              <label for="manager-ts-customer-filter" class="hrTimesheets-required-label">Customer</label>
              <select
                id="manager-ts-customer-filter"
                v-model="tsFilterCustomerPk"
                class="hrTimesheets-customer-select manager-ts-hr-select"
              >
                <option value="">All customers</option>
                <option v-for="opt in managerTsCustomerPartyOptions" :key="'c-' + opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="hrTimesheets-filter-group">
              <label for="manager-ts-vendor-filter" class="hrTimesheets-required-label">Vendor</label>
              <select
                id="manager-ts-vendor-filter"
                v-model="tsFilterVendorPk"
                class="hrTimesheets-customer-select manager-ts-hr-select"
              >
                <option value="">All vendors</option>
                <option v-for="opt in managerTsVendorPartyOptions" :key="'v-' + opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="hrTimesheets-main-content manager-ts-single-column">
          <div class="hrTimesheets-consultant-section">
            <div class="hrTimesheets-consultant-header-container">
              <div class="hrTimesheets-consultant-header">
                <div class="hrTimesheets-header-label-row">
                  <span class="hrTimesheets-required-label manager-ts-consultant-section-title">Timesheets</span>
                </div>
                <div class="hrTimesheets-header-actions">
                  <span v-if="!loading" class="manager-ts-header-meta">
                    {{ managerTimesheetPaginationTotal.toLocaleString() }} {{ managerTimesheetListLabel }}
                  </span>
                </div>
              </div>
            </div>
            <div class="hr-timesheets-content-wrapper">
              <div v-if="loading" class="hrTimesheets-loading-state manager-ts-loading-pad">
                <div class="hrTimesheets-loading-spinner"></div>
                <span>Loading timesheets…</span>
              </div>
              <div v-else-if="!rows.length" class="hrTimesheets-no-data-state">
                <i class="bi bi-inbox"></i>
                <span>No timesheets found</span>
              </div>
              <template v-else>
                <div class="hrTimesheets-consultant-grid manager-ts-timesheet-grid">
                  <ManagerTimesheetCard
                    v-for="sheet in paginatedTimesheetRows"
                    :key="rowKey(sheet)"
                    :sheet="sheet"
                    @edit="openEdit"
                    @open-attachment="onManagerTimesheetCardOpenAttachment"
                  />
                </div>
                <div class="hr-timesheets-pagination-wrapper">
                  <PaginationComponent
                    :current-page="tsCurrentPage"
                    :total-items="managerTimesheetPaginationTotal"
                    :items-per-page="tsItemsPerPage"
                    :item-label="tsListSource === 'hr' ? 'HR timesheets' : 'Consultant timesheets'"
                    :show-items-per-page="true"
                    @page-change="handleManagerTsPageChange"
                    @items-per-page-change="handleManagerTsItemsPerPageChange"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>

    <ManagerCompanyRecordsEditModal
      :show="editOpen"
      :is-timesheets-view="isManagerTimesheetsView"
      :is-invoices-view="routeName === 'ManagerCompanyInvoices'"
      :title="editModalTitle"
      :edit-row="editRow"
      :edit-form="editForm"
      :record-edit-fields="editFields"
      :timesheet-edit-fields="timesheetModalEditFields"
      :can-edit-timesheet-period="canEditManagerTimesheetPeriod"
      :timesheet-period-lock-hint="managerTimesheetPeriodLockHint"
      :edit-timesheet-list-source="editTimesheetListSource"
      :timesheet-attachments="timesheetEditAttachments"
      :timesheet-attachments-busy="timesheetAttachmentsBusy"
      :timesheet-attachment-error="timesheetAttachmentError"
      :timesheet-attachment-success="timesheetAttachmentSuccess"
      :save-error="saveError"
      :saving="saving"
      @close="closeEdit"
      @save="saveEdit"
      @patch-edit-form="patchEditForm"
      @view-attachment="viewManagerTimesheetAttachment"
      @download-attachment="downloadManagerTimesheetAttachment"
      @add-timesheet-attachment="addManagerTimesheetAttachment"
      @delete-timesheet-attachment="deleteManagerTimesheetAttachment"
      @rename-timesheet-attachment="renameManagerTimesheetAttachment"
    />

    <ConfirmModal
      :show="showAttachmentDeleteConfirm"
      title="Delete attachment"
      :message="attachmentDeleteConfirmMessage"
      confirm-text="Delete"
      @cancel="cancelAttachmentDeleteConfirm"
      @confirm="confirmAttachmentDelete"
    />
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import DatePicker from '@/components/DatePicker.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import ManagerTimesheetCard from '../components/ManagerTimesheetCard.vue';
import ManagerCompanyRecordsEditModal from '../components/ManagerCompanyRecordsEditModal.vue';
import { formatIsoToUs as formatIsoToUsHelper, parseUsToIso as parseUsToIsoHelper } from '../utils/managerTimesheetCardHelpers';
import '@/modules/consultant/styles/HRTimesheets.css';
import '../styles/ManagerCompanyRecordsView.css';
import '../styles/ManagerTimesheetCardsGrid.css';

dayjs.extend(isoWeek);

/** Matches backend: period/hours only updatable in this status */
const MANAGER_TIMESHEET_PERIOD_EDITABLE_STATUS = 'Time Sheet Created';

/** Select value when agreement has no customer/vendor FK */
const TS_PARTY_NULL_PK = '__null__';

const MANAGER_HR_TIMESHEET_EDIT_FIELDS = [
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'Submitted', label: 'Submitted' },
      { value: 'Approved', label: 'Approved' },
      { value: 'Processed', label: 'Processed' }
    ]
  }
];

const ROUTE_CONFIG = {
  ManagerCompanyConsultants: {
    title: 'Consultants',
    apiSegment: 'consultants',
    patchSegment: 'consultants',
    idKey: 'id',
    columns: [
      { key: 'consultantId', label: 'ID' },
      { key: 'consultantName', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'consultantType', label: 'Type' },
      { key: 'active', label: 'Active', bool: true }
    ],
    editFields: [
      { key: 'consultantName', label: 'Name' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      {
        key: 'consultantType',
        label: 'Type',
        type: 'select',
        options: [
          { value: 'Fulltime', label: 'Fulltime' },
          { value: '1099', label: '1099' },
          { value: 'C2C', label: 'C2C' }
        ]
      },
      { key: 'salesDescription', label: 'Sales description', type: 'textarea' },
      { key: 'salesPrice', label: 'Sales price', type: 'number' },
      { key: 'active', label: 'Active', type: 'checkbox' }
    ]
  },
  ManagerCompanyCustomers: {
    title: 'Customers',
    apiSegment: 'customers',
    patchSegment: 'customers',
    idKey: 'id',
    columns: [
      { key: 'customerId', label: 'ID' },
      { key: 'customerName', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'balance', label: 'Balance' },
      { key: 'active', label: 'Active', bool: true }
    ],
    editFields: [
      { key: 'customerName', label: 'Name' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
      { key: 'active', label: 'Active', type: 'checkbox' }
    ]
  },
  ManagerCompanyVendors: {
    title: 'Vendors',
    apiSegment: 'vendors',
    patchSegment: 'vendors',
    idKey: 'id',
    columns: [
      { key: 'vendorId', label: 'ID' },
      { key: 'vendorName', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'balance', label: 'Balance' },
      { key: 'active', label: 'Active', bool: true }
    ],
    editFields: [
      { key: 'vendorName', label: 'Name' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'phone', label: 'Phone' },
      { key: 'address', label: 'Address' },
      { key: 'taxId', label: 'Tax ID' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
      { key: 'active', label: 'Active', type: 'checkbox' }
    ]
  },
  ManagerCompanyInvoices: {
    title: 'Invoices',
    apiSegment: 'invoices',
    patchSegment: 'invoices',
    idKey: 'id',
    columns: [
      { key: 'invoiceNo', label: 'Invoice #' },
      { key: 'date', label: 'Invoice date', dateUS: true },
      { key: 'dueDate', label: 'Due date', dateUS: true },
      { key: 'customerName', label: 'Customer' },
      { key: 'status', label: 'Status' }
    ],
    editFields: [
      { key: 'date', label: 'Invoice date', type: 'date' },
      { key: 'dueDate', label: 'Due date', type: 'date' },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'draft', label: 'Draft' },
          { value: 'sent', label: 'Sent' },
          { value: 'paid', label: 'Paid' },
          { value: 'partially_paid', label: 'Partially paid' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'cancelled', label: 'Cancelled' }
        ]
      },
      { key: 'orderNumber', label: 'Order #' },
      { key: 'contact', label: 'Contact' },
      { key: 'phoneNumber', label: 'Phone' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'billTo', label: 'Bill to', type: 'textarea', alwaysSendInPayload: true },
      { key: 'comments', label: 'Comments', type: 'textarea', alwaysSendInPayload: true }
    ]
  },
  ManagerCompanyTimesheets: {
    title: 'Timesheets',
    apiSegment: 'timesheets',
    patchSegment: 'timesheets',
    idKey: 'id',
    columns: [
      { key: 'consultantName', label: 'Consultant' },
      { key: 'consultantType', label: 'Type' },
      { key: 'customerName', label: 'Customer' },
      { key: 'vendorName', label: 'Vendor' },
      { key: 'fromDate', label: 'From', dateUS: true },
      { key: 'toDate', label: 'To', dateUS: true },
      { key: 'totalHours', label: 'Hours' },
      { key: 'status', label: 'Status' },
      { key: 'billStatus', label: 'Bill status' },
      { key: 'attachmentCount', label: 'Attachments' }
    ],
    editFields: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'Time Sheet Created', label: 'Time Sheet Created' },
          { value: 'Invoice Done', label: 'Invoice Done' },
          { value: 'Payment Received', label: 'Payment Received' },
          { value: 'Partial Payment Received', label: 'Partial Payment Received' }
        ]
      },
      {
        key: 'billStatus',
        label: 'Bill status',
        type: 'select',
        options: [
          { value: 'Not Applicable', label: 'Not Applicable' },
          { value: 'Bill Created', label: 'Bill Created' },
          { value: 'Bill Paid', label: 'Bill Paid' }
        ]
      }
    ]
  },
  ManagerCompanyBills: {
    title: 'Bills',
    apiSegment: 'bills',
    patchSegment: 'bills',
    idKey: 'id',
    columns: [
      { key: 'billNo', label: 'Bill #' },
      { key: 'vendorName', label: 'Vendor' },
      { key: 'date', label: 'Date' },
      { key: 'total', label: 'Total' },
      { key: 'status', label: 'Status' }
    ],
    editFields: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'Open', label: 'Open' },
          { value: 'Partial', label: 'Partial' },
          { value: 'Paid', label: 'Paid' },
          { value: 'Overdue', label: 'Overdue' },
          { value: 'Cancelled', label: 'Cancelled' },
          { value: 'Disputed', label: 'Disputed' },
          { value: 'On Hold', label: 'On Hold' }
        ]
      },
      { key: 'dueDate', label: 'Due date', type: 'date' },
      { key: 'contactName', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'comments', label: 'Comments', type: 'textarea' }
    ]
  }
};

export default {
  name: 'ManagerCompanyRecordsView',
  components: {
    DatePicker,
    PaginationComponent,
    ConfirmModal,
    ManagerTimesheetCard,
    ManagerCompanyRecordsEditModal
  },
  data() {
    return {
      rows: [],
      loading: true,
      loadError: null,
      editOpen: false,
      editRow: null,
      editForm: {},
      saving: false,
      saveError: null,
      tsDatePreset: 'all',
      tsFromDate: '',
      tsToDate: '',
      tsStatus: '',
      tsBillStatus: '',
      timesheetFetchTimer: null,
      tsListSource: 'consultant',
      editTimesheetListSource: 'consultant',
      timesheetAttachmentsBusy: false,
      timesheetAttachmentError: '',
      timesheetAttachmentSuccess: '',
      timesheetCardAttachmentError: '',
      attachmentSuccessTimer: null,
      showAttachmentDeleteConfirm: false,
      attachmentDeleteContext: null,
      tsCurrentPage: 1,
      tsItemsPerPage: 10,
      tsFilterConsultantPk: '',
      tsFilterCustomerPk: '',
      tsFilterVendorPk: '',
      consultantFilterOptions: [],
      hrConsultantFilterOptions: [],
      managerTsAgreementOptions: [],
      recordsCurrentPage: 1,
      recordsItemsPerPage: 25,
      recordsTotal: 0,
      recordsSearch: '',
      recordsStatus: '',
      recordsFromDate: '',
      recordsToDate: '',
      recordsCustomerId: '',
      recordsVendorId: '',
      recordsDatePreset: 'all',
      recordsFetchTimer: null,
      recordFilterCustomers: [],
      recordFilterVendors: []
    };
  },
  computed: {
    routeName() {
      return this.$route.name;
    },
    isManagerTimesheetsView() {
      return this.routeName === 'ManagerCompanyTimesheets';
    },
    isManagerInvoicesView() {
      return this.routeName === 'ManagerCompanyInvoices';
    },
    isManagerBillsView() {
      return this.routeName === 'ManagerCompanyBills';
    },
    isManagerRecordsListView() {
      return this.isManagerInvoicesView || this.isManagerBillsView;
    },
    recordsSearchInputId() {
      return this.isManagerInvoicesView ? 'manager-records-invoice-search' : 'manager-records-bill-search';
    },
    isRecordsDateRangeValid() {
      if (!this.recordsFromDate || !this.recordsToDate) return true;
      return this.recordsFromDate <= this.recordsToDate;
    },
    recordsFromDateUI: {
      get() {
        return this.formatIsoToUs(this.recordsFromDate);
      },
      set(usDate) {
        this.recordsFromDate = this.parseUsToIso(usDate);
      }
    },
    recordsToDateUI: {
      get() {
        return this.formatIsoToUs(this.recordsToDate);
      },
      set(usDate) {
        this.recordsToDate = this.parseUsToIso(usDate);
      }
    },
    recordsStatusFilterOptions() {
      if (this.isManagerInvoicesView) {
        const statusField = (ROUTE_CONFIG.ManagerCompanyInvoices?.editFields || []).find((f) => f.key === 'status');
        return statusField?.options || [];
      }
      if (this.isManagerBillsView) {
        const statusField = (ROUTE_CONFIG.ManagerCompanyBills?.editFields || []).find((f) => f.key === 'status');
        return statusField?.options || [];
      }
      return [];
    },
    recordsListLabel() {
      if (this.isManagerInvoicesView) {
        return this.recordsTotal === 1 ? 'invoice' : 'invoices';
      }
      return this.recordsTotal === 1 ? 'bill' : 'bills';
    },
    isTimesheetDateRangeValid() {
      if (!this.tsFromDate || !this.tsToDate) return true;
      return this.tsFromDate <= this.tsToDate;
    },
    tsFromDateUI: {
      get() {
        return this.formatIsoToUs(this.tsFromDate);
      },
      set(usDate) {
        this.tsFromDate = this.parseUsToIso(usDate);
      }
    },
    tsToDateUI: {
      get() {
        return this.formatIsoToUs(this.tsToDate);
      },
      set(usDate) {
        this.tsToDate = this.parseUsToIso(usDate);
      }
    },
    config() {
      return ROUTE_CONFIG[this.routeName] || null;
    },
    clientId() {
      return this.$route.params.clientId;
    },
    companyId() {
      return this.$route.params.companyId;
    },
    companyName() {
      return this.$route.query.companyName || '';
    },
    managerCompanyNavLinks() {
      return [
        { routeName: 'ManagerCompanyTimesheets', label: 'Timesheets' },
        { routeName: 'ManagerCompanyInvoices', label: 'Invoices' },
        { routeName: 'ManagerCompanyBills', label: 'Bills' }
      ];
    },
    pageTitle() {
      return this.config ? this.config.title : 'Records';
    },
    columns() {
      return this.config ? this.config.columns : [];
    },
    /** HR timesheets have no bill workflow — hide Bill status column. */
    tableColumns() {
      const cols = this.columns;
      if (this.isManagerTimesheetsView && this.tsListSource === 'hr') {
        return cols.filter((c) => c.key !== 'billStatus');
      }
      return cols;
    },
    editFields() {
      return this.config ? this.config.editFields : [];
    },
    managerTimesheetApiSegment() {
      if (!this.isManagerTimesheetsView) return 'timesheets';
      return this.tsListSource === 'hr' ? 'hr-timesheets' : 'timesheets';
    },
    timesheetModalEditFields() {
      if (!this.isManagerTimesheetsView) return this.editFields;
      return this.editTimesheetListSource === 'hr' ? MANAGER_HR_TIMESHEET_EDIT_FIELDS : this.editFields;
    },
    managerTimesheetPeriodLockHint() {
      return this.editTimesheetListSource === 'hr'
        ? 'From, To, and Hours can only be edited when Status is "Submitted".'
        : 'From, To, and Hours can only be edited when Status is "Time Sheet Created".';
    },
    editModalTitle() {
      if (this.isManagerTimesheetsView) return 'Edit timesheet';
      if (this.routeName === 'ManagerCompanyInvoices') return 'Edit invoice';
      return 'Edit';
    },
    timesheetEditAttachments() {
      if (!this.editRow || !Array.isArray(this.editRow.attachments)) return [];
      return this.editRow.attachments;
    },
    /** Sheet shape for client-manager attachment API (includes listSource for HR vs consultant). */
    modalTimesheetSheetRef() {
      if (!this.editRow?.id) return null;
      return {
        ...this.editRow,
        listSource:
          this.editRow.listSource ||
          (this.editTimesheetListSource === 'hr' ? 'hr' : 'consultant')
      };
    },
    attachmentDeleteConfirmMessage() {
      const a = this.attachmentDeleteContext && this.attachmentDeleteContext.attachment;
      if (!a) return ' ';
      const name = a.fileName != null && String(a.fileName).trim() !== '' ? String(a.fileName).trim() : 'this file';
      return `Remove "${name}" from this timesheet? This cannot be undone.`;
    },
    canEditManagerTimesheetPeriod() {
      if (!this.isManagerTimesheetsView || !this.editRow) return false;
      const s = this.editForm && this.editForm.status;
      if (this.editTimesheetListSource === 'hr') return s === 'Submitted';
      return s === MANAGER_TIMESHEET_PERIOD_EDITABLE_STATUS;
    },
    managerTsConsultantSelectOptions() {
      return this.tsListSource === 'hr' ? this.hrConsultantFilterOptions : this.consultantFilterOptions;
    },
    showManagerTsPartyFilters() {
      return (
        this.isManagerTimesheetsView &&
        (this.tsListSource === 'consultant' || this.tsListSource === 'hr') &&
        Boolean(this.tsFilterConsultantPk) &&
        this.managerTsAgreementOptions.length > 1
      );
    },
    managerTsCustomerPartyOptions() {
      if (!this.showManagerTsPartyFilters) return [];
      const map = new Map();
      for (const a of this.managerTsAgreementOptions) {
        const value = a.customerPk == null ? TS_PARTY_NULL_PK : String(a.customerPk);
        if (!map.has(value)) {
          map.set(value, {
            value,
            label: a.customerName || '—'
          });
        }
      }
      return Array.from(map.values()).sort((x, y) =>
        x.label.localeCompare(y.label, undefined, { sensitivity: 'base' })
      );
    },
    managerTsVendorPartyOptions() {
      if (!this.showManagerTsPartyFilters) return [];
      let list = this.managerTsAgreementOptions;
      if (this.tsFilterCustomerPk) {
        if (this.tsFilterCustomerPk === TS_PARTY_NULL_PK) {
          list = list.filter((a) => a.customerPk == null);
        } else {
          const ck = parseInt(String(this.tsFilterCustomerPk), 10);
          if (Number.isFinite(ck)) {
            list = list.filter((a) => a.customerPk === ck);
          }
        }
      }
      const map = new Map();
      for (const a of list) {
        const value = a.vendorPk == null ? TS_PARTY_NULL_PK : String(a.vendorPk);
        if (!map.has(value)) {
          map.set(value, {
            value,
            label: a.vendorName || '—'
          });
        }
      }
      return Array.from(map.values()).sort((x, y) =>
        x.label.localeCompare(y.label, undefined, { sensitivity: 'base' })
      );
    },
    managerTimesheetsFilteredRows() {
      if (!this.isManagerTimesheetsView || !Array.isArray(this.rows)) return [];
      return this.rows;
    },
    managerTimesheetPaginationTotal() {
      return this.managerTimesheetsFilteredRows.length;
    },
    managerTimesheetListLabel() {
      const n = this.managerTimesheetPaginationTotal;
      if (this.tsListSource === 'hr') {
        return n === 1 ? 'HR timesheet' : 'HR timesheets';
      }
      return n === 1 ? 'Consultant timesheet' : 'Consultant timesheets';
    },
    paginatedTimesheetRows() {
      const list = this.managerTimesheetsFilteredRows;
      const start = (this.tsCurrentPage - 1) * this.tsItemsPerPage;
      return list.slice(start, start + this.tsItemsPerPage);
    }
  },
  watch: {
    '$route.fullPath'() {
      this.resetRecordsFilters();
      this.fetchRows();
      if (this.isManagerRecordsListView) {
        void this.fetchRecordFilterOptions();
      }
    },
    recordsSearch() {
      if (!this.isManagerRecordsListView) return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsStatus() {
      if (!this.isManagerRecordsListView) return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsCustomerId() {
      if (!this.isManagerInvoicesView) return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsVendorId() {
      if (!this.isManagerBillsView) return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsFromDate() {
      if (!this.isManagerRecordsListView || this.recordsDatePreset !== 'custom') return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsToDate() {
      if (!this.isManagerRecordsListView || this.recordsDatePreset !== 'custom') return;
      this.recordsCurrentPage = 1;
      this.queueRecordsFetch();
    },
    recordsDatePreset: {
      handler() {
        if (!this.isManagerRecordsListView) return;
        this.recordsCurrentPage = 1;
        const val = this.recordsDatePreset;
        if (val === 'custom') {
          this.queueRecordsFetch();
          return;
        }
        const today = dayjs();
        let from = '';
        let to = '';
        switch (val) {
          case 'all':
            break;
          case 'this_month':
            from = today.startOf('month').format('YYYY-MM-DD');
            to = today.endOf('month').format('YYYY-MM-DD');
            break;
          case 'last_month': {
            const m = today.subtract(1, 'month');
            from = m.startOf('month').format('YYYY-MM-DD');
            to = m.endOf('month').format('YYYY-MM-DD');
            break;
          }
          case 'this_quarter': {
            const month = today.month();
            const qStart = Math.floor(month / 3) * 3;
            from = today.month(qStart).startOf('month').format('YYYY-MM-DD');
            to = today.month(qStart + 2).endOf('month').format('YYYY-MM-DD');
            break;
          }
          case 'this_year':
            from = today.startOf('year').format('YYYY-MM-DD');
            to = today.endOf('year').format('YYYY-MM-DD');
            break;
          default:
            break;
        }
        this.recordsFromDate = from;
        this.recordsToDate = to;
        this.queueRecordsFetch();
      }
    },
    tsDatePreset: {
      immediate: true,
      handler() {
        const val = this.tsDatePreset;
        if (this.routeName !== 'ManagerCompanyTimesheets') return;
        this.tsCurrentPage = 1;
        if (val === 'custom') {
          this.queueTimesheetFetch();
          return;
        }
        const today = dayjs();
        let from = '';
        let to = '';
        let quarter;
        switch (val) {
          case 'all':
            break;
          case 'this_week':
            from = today.startOf('isoWeek').format('YYYY-MM-DD');
            to = today.endOf('isoWeek').format('YYYY-MM-DD');
            break;
          case 'last_week':
            from = today.subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
            to = today.subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD');
            break;
          case 'this_month':
            from = today.startOf('month').format('YYYY-MM-DD');
            to = today.endOf('month').format('YYYY-MM-DD');
            break;
          case 'last_month':
            from = today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
            to = today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
            break;
          case 'this_quarter':
            quarter = Math.floor(today.month() / 3);
            from = dayjs(new Date(today.year(), quarter * 3, 1)).format('YYYY-MM-DD');
            to = dayjs(new Date(today.year(), quarter * 3 + 3, 0)).format('YYYY-MM-DD');
            break;
          case 'this_year':
            from = today.startOf('year').format('YYYY-MM-DD');
            to = today.endOf('year').format('YYYY-MM-DD');
            break;
          default:
            return;
        }
        this.tsFromDate = from;
        this.tsToDate = to;
        this.queueTimesheetFetch();
      }
    },
    tsFromDate() {
      if (this.isManagerTimesheetsView) {
        this.tsCurrentPage = 1;
        this.queueTimesheetFetch();
      }
    },
    tsToDate() {
      if (this.isManagerTimesheetsView) {
        this.tsCurrentPage = 1;
        this.queueTimesheetFetch();
      }
    },
    tsStatus() {
      if (this.isManagerTimesheetsView) {
        this.tsCurrentPage = 1;
        this.queueTimesheetFetch();
      }
    },
    tsBillStatus() {
      if (this.isManagerTimesheetsView) {
        this.tsCurrentPage = 1;
        this.queueTimesheetFetch();
      }
    },
    tsListSource() {
      if (!this.isManagerTimesheetsView) return;
      this.tsStatus = '';
      this.tsBillStatus = '';
      this.tsFilterConsultantPk = '';
      this.tsFilterCustomerPk = '';
      this.tsFilterVendorPk = '';
      this.managerTsAgreementOptions = [];
      this.tsCurrentPage = 1;
      if (this.editOpen) this.closeEdit();
      this.queueTimesheetFetch();
    },
    tsFilterConsultantPk() {
      if (!this.isManagerTimesheetsView) return;
      this.tsCurrentPage = 1;
      if (this.tsListSource === 'consultant') {
        this.tsFilterCustomerPk = '';
        this.tsFilterVendorPk = '';
        this.managerTsAgreementOptions = [];
        const sync = async () => {
          if (this.tsFilterConsultantPk) {
            await this.fetchManagerTsAgreementOptions();
          }
          this.queueTimesheetFetch();
        };
        void sync();
        return;
      }
      if (this.tsListSource === 'hr') {
        this.tsFilterCustomerPk = '';
        this.tsFilterVendorPk = '';
        this.managerTsAgreementOptions = [];
        const syncHr = async () => {
          if (this.tsFilterConsultantPk) {
            await this.fetchManagerTsAgreementOptions();
          }
          this.queueTimesheetFetch();
        };
        void syncHr();
      }
    },
    tsFilterCustomerPk() {
      if (!this.isManagerTimesheetsView) return;
      this.tsFilterVendorPk = '';
      this.tsCurrentPage = 1;
      if (this.tsListSource === 'consultant' || this.tsListSource === 'hr') {
        this.queueTimesheetFetch();
      }
    },
    tsFilterVendorPk() {
      if (!this.isManagerTimesheetsView) return;
      this.tsCurrentPage = 1;
      if (this.tsListSource === 'consultant' || this.tsListSource === 'hr') {
        this.queueTimesheetFetch();
      }
    }
  },
  mounted() {
    this.fetchRows();
    if (this.isManagerRecordsListView) {
      void this.fetchRecordFilterOptions();
    }
  },
  beforeUnmount() {
    if (this.timesheetFetchTimer) {
      clearTimeout(this.timesheetFetchTimer);
    }
    if (this.recordsFetchTimer) {
      clearTimeout(this.recordsFetchTimer);
    }
    if (this.attachmentSuccessTimer) {
      clearTimeout(this.attachmentSuccessTimer);
      this.attachmentSuccessTimer = null;
    }
  },
  methods: {
    baseUrl() {
      return `${process.env.VUE_APP_API_URL}/client-manager/clients/${this.clientId}/company/${this.companyId}`;
    },
    formatIsoToUs(isoDate) {
      return formatIsoToUsHelper(isoDate);
    },
    parseUsToIso(usDate) {
      return parseUsToIsoHelper(usDate);
    },
    normalizeRowDateIso(value) {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'string') {
        return value.length >= 10 ? value.slice(0, 10) : value;
      }
      const d = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    },
    queueTimesheetFetch() {
      if (!this.isManagerTimesheetsView) return;
      if (!this.isTimesheetDateRangeValid) return;
      clearTimeout(this.timesheetFetchTimer);
      this.timesheetFetchTimer = setTimeout(() => {
        this.timesheetFetchTimer = null;
        this.fetchRows();
      }, 100);
    },
    queueRecordsFetch() {
      if (!this.isManagerRecordsListView) return;
      if (!this.isRecordsDateRangeValid) return;
      clearTimeout(this.recordsFetchTimer);
      this.recordsFetchTimer = setTimeout(() => {
        this.recordsFetchTimer = null;
        this.fetchRows();
      }, 300);
    },
    buildRecordsQueryParams() {
      const params = {
        page: this.recordsCurrentPage,
        limit: this.recordsItemsPerPage
      };
      const search = this.recordsSearch.trim();
      if (search) params.search = search;
      if (this.recordsStatus) params.status = this.recordsStatus;
      if (this.isManagerInvoicesView && this.recordsCustomerId) {
        params.customerId = this.recordsCustomerId;
      }
      if (this.isManagerBillsView && this.recordsVendorId) {
        params.vendorId = this.recordsVendorId;
      }
      if (this.recordsDatePreset !== 'all') {
        if (this.recordsFromDate) params.fromDate = this.recordsFromDate;
        if (this.recordsToDate) params.toDate = this.recordsToDate;
      }
      return params;
    },
    resetRecordsFilters() {
      this.recordsCurrentPage = 1;
      this.recordsItemsPerPage = 25;
      this.recordsTotal = 0;
      this.recordsSearch = '';
      this.recordsStatus = '';
      this.recordsFromDate = '';
      this.recordsToDate = '';
      this.recordsCustomerId = '';
      this.recordsVendorId = '';
      this.recordsDatePreset = 'all';
    },
    clearRecordsFilters() {
      this.resetRecordsFilters();
      this.queueRecordsFetch();
    },
    handleRecordsPageChange(page) {
      this.recordsCurrentPage = page;
      this.fetchRows();
    },
    handleRecordsItemsPerPageChange(perPage) {
      this.recordsItemsPerPage = perPage;
      this.recordsCurrentPage = 1;
      this.fetchRows();
    },
    async fetchRecordFilterOptions() {
      if (!this.isManagerRecordsListView) return;
      try {
        if (this.isManagerInvoicesView) {
          const { data } = await axios.get(`${this.baseUrl()}/customers`);
          this.recordFilterCustomers = Array.isArray(data) ? data : [];
          this.recordFilterVendors = [];
        } else if (this.isManagerBillsView) {
          const { data } = await axios.get(`${this.baseUrl()}/vendors`);
          this.recordFilterVendors = Array.isArray(data) ? data : [];
          this.recordFilterCustomers = [];
        }
      } catch {
        this.recordFilterCustomers = [];
        this.recordFilterVendors = [];
      }
    },
    buildTimesheetQueryParams() {
      const params = {};
      if (this.tsStatus) params.status = this.tsStatus;
      if (this.tsListSource === 'consultant' && this.tsBillStatus) params.billStatus = this.tsBillStatus;
      if (this.tsDatePreset !== 'all') {
        if (this.tsFromDate) params.fromDate = this.tsFromDate;
        if (this.tsToDate) params.toDate = this.tsToDate;
      }
      if (this.tsListSource === 'consultant') {
        if (this.tsFilterConsultantPk) {
          const cpk = parseInt(String(this.tsFilterConsultantPk), 10);
          if (Number.isFinite(cpk)) params.consultantPk = cpk;
        }
        if (this.tsFilterCustomerPk && this.tsFilterCustomerPk !== TS_PARTY_NULL_PK) {
          const cust = parseInt(String(this.tsFilterCustomerPk), 10);
          if (Number.isFinite(cust)) params.customerPk = cust;
        }
        if (this.tsFilterVendorPk && this.tsFilterVendorPk !== TS_PARTY_NULL_PK) {
          const vend = parseInt(String(this.tsFilterVendorPk), 10);
          if (Number.isFinite(vend)) params.vendorPk = vend;
        }
      } else if (this.tsListSource === 'hr' && this.tsFilterConsultantPk) {
        const cpk = parseInt(String(this.tsFilterConsultantPk), 10);
        if (Number.isFinite(cpk)) params.consultantPk = cpk;
        if (this.tsFilterCustomerPk && this.tsFilterCustomerPk !== TS_PARTY_NULL_PK) {
          const cust = parseInt(String(this.tsFilterCustomerPk), 10);
          if (Number.isFinite(cust)) params.customerPk = cust;
        }
        if (this.tsFilterVendorPk && this.tsFilterVendorPk !== TS_PARTY_NULL_PK) {
          const vend = parseInt(String(this.tsFilterVendorPk), 10);
          if (Number.isFinite(vend)) params.vendorPk = vend;
        }
      }
      return params;
    },
    /** Same as timesheet list filters but without consultant / customer / vendor (for “who has timesheets” dropdown). */
    buildTimesheetConsultantFilterQueryParams() {
      const params = {};
      if (this.tsStatus) params.status = this.tsStatus;
      if (this.tsBillStatus) params.billStatus = this.tsBillStatus;
      if (this.tsDatePreset !== 'all') {
        if (this.tsFromDate) params.fromDate = this.tsFromDate;
        if (this.tsToDate) params.toDate = this.tsToDate;
      }
      return params;
    },
    /** HR consultant dropdown: status + date range only (matches listHRTimesheetConsultantFilterOptions). */
    buildHRTimesheetConsultantFilterQueryParams() {
      const params = {};
      if (this.tsStatus) params.status = this.tsStatus;
      if (this.tsDatePreset !== 'all') {
        if (this.tsFromDate) params.fromDate = this.tsFromDate;
        if (this.tsToDate) params.toDate = this.tsToDate;
      }
      return params;
    },
    managerTsConsultantOptionLabel(c) {
      return c.consultantName || 'Consultant';
    },
    async fetchHrConsultantsForTsFilter() {
      if (!this.isManagerTimesheetsView || this.tsListSource !== 'hr') {
        this.hrConsultantFilterOptions = [];
        return;
      }
      if (!this.isTimesheetDateRangeValid) {
        this.hrConsultantFilterOptions = [];
        return;
      }
      try {
        const { data } = await axios.get(`${this.baseUrl()}/hr-timesheets/consultant-filter-options`, {
          params: this.buildHRTimesheetConsultantFilterQueryParams()
        });
        const list = Array.isArray(data?.consultants) ? data.consultants : [];
        const pkSet = new Set(list.map((c) => c.consultantPk));
        let clearedConsultant = false;
        if (this.tsFilterConsultantPk) {
          const cur = parseInt(String(this.tsFilterConsultantPk), 10);
          if (!Number.isFinite(cur) || !pkSet.has(cur)) {
            this.tsFilterConsultantPk = '';
            clearedConsultant = true;
          }
        }
        this.hrConsultantFilterOptions = list.map((c) => ({
          pk: c.consultantPk,
          label: this.managerTsConsultantOptionLabel(c)
        }));
        if (clearedConsultant) {
          this.queueTimesheetFetch();
        }
      } catch {
        this.hrConsultantFilterOptions = [];
      }
    },
    async fetchConsultantsForTsFilter() {
      if (!this.isManagerTimesheetsView) return;
      if (this.tsListSource === 'hr') {
        this.consultantFilterOptions = [];
        await this.fetchHrConsultantsForTsFilter();
        return;
      }
      if (this.tsListSource !== 'consultant') {
        this.consultantFilterOptions = [];
        return;
      }
      this.hrConsultantFilterOptions = [];
      if (!this.isTimesheetDateRangeValid) {
        this.consultantFilterOptions = [];
        return;
      }
      try {
        const { data } = await axios.get(`${this.baseUrl()}/timesheets/consultant-filter-options`, {
          params: this.buildTimesheetConsultantFilterQueryParams()
        });
        const list = Array.isArray(data?.consultants) ? data.consultants : [];
        const pkSet = new Set(list.map((c) => c.consultantPk));
        let clearedConsultant = false;
        if (this.tsFilterConsultantPk) {
          const cur = parseInt(String(this.tsFilterConsultantPk), 10);
          if (!Number.isFinite(cur) || !pkSet.has(cur)) {
            this.tsFilterConsultantPk = '';
            this.tsFilterCustomerPk = '';
            this.tsFilterVendorPk = '';
            this.managerTsAgreementOptions = [];
            clearedConsultant = true;
          }
        }
        this.consultantFilterOptions = list.map((c) => ({
          pk: c.consultantPk,
          label: this.managerTsConsultantOptionLabel(c)
        }));
        if (clearedConsultant) {
          this.queueTimesheetFetch();
        }
      } catch {
        this.consultantFilterOptions = [];
      }
    },
    async fetchManagerTsAgreementOptions() {
      this.managerTsAgreementOptions = [];
      if (!this.tsFilterConsultantPk) return;
      try {
        const { data } = await axios.get(`${this.baseUrl()}/timesheets/agreement-options`, {
          params: { consultantPk: this.tsFilterConsultantPk }
        });
        this.managerTsAgreementOptions = Array.isArray(data?.agreements) ? data.agreements : [];
      } catch {
        this.managerTsAgreementOptions = [];
      }
    },
    setTimesheetListSource(source) {
      if (this.tsListSource === source) return;
      this.tsListSource = source;
    },
    async fetchRows() {
      if (!this.config) {
        this.loading = false;
        this.loadError = 'Unknown page.';
        return;
      }
      if (this.isManagerTimesheetsView && !this.isTimesheetDateRangeValid) {
        this.rows = [];
        this.consultantFilterOptions = [];
        this.hrConsultantFilterOptions = [];
        this.loading = false;
        return;
      }
      if (this.isManagerRecordsListView && !this.isRecordsDateRangeValid) {
        this.rows = [];
        this.recordsTotal = 0;
        this.loading = false;
        return;
      }
      this.loading = true;
      this.loadError = null;
      try {
        const segment = this.isManagerTimesheetsView ? this.managerTimesheetApiSegment : this.config.apiSegment;
        const url = `${this.baseUrl()}/${segment}`;
        const config = this.isManagerTimesheetsView
          ? { params: this.buildTimesheetQueryParams() }
          : this.isManagerRecordsListView
            ? { params: this.buildRecordsQueryParams() }
            : {};
        const { data } = await axios.get(url, config);
        if (this.isManagerRecordsListView && data && typeof data === 'object' && Array.isArray(data.data)) {
          this.rows = data.data;
          this.recordsTotal = Number(data.total) || 0;
          this.$nextTick(() => {
            const per = Math.max(1, this.recordsItemsPerPage);
            const maxPage = Math.max(1, Math.ceil(this.recordsTotal / per));
            if (this.recordsCurrentPage > maxPage) this.recordsCurrentPage = maxPage;
          });
        } else {
          this.rows = Array.isArray(data) ? data : [];
          if (this.isManagerRecordsListView) {
            this.recordsTotal = this.rows.length;
          }
        }
        if (this.isManagerTimesheetsView) {
          void this.fetchConsultantsForTsFilter();
        }
        if (this.isManagerTimesheetsView) {
          this.$nextTick(() => {
            const per = Math.max(1, this.tsItemsPerPage);
            const total = this.managerTimesheetPaginationTotal;
            const maxPage = Math.max(1, Math.ceil(total / per));
            if (this.tsCurrentPage > maxPage) this.tsCurrentPage = maxPage;
          });
        }
      } catch (e) {
        this.rows = [];
        this.recordsTotal = 0;
        this.loadError = e.response?.data?.message || 'Failed to load records.';
      } finally {
        this.loading = false;
      }
    },
    rowKey(row) {
      if (this.isManagerTimesheetsView && row.listSource === 'hr') {
        return `hr-${row[this.config.idKey]}`;
      }
      return row[this.config.idKey];
    },
    cellValue(row, col) {
      if (col.key === 'customerName') {
        if (row.customer) return row.customer.customerName || '—';
        return row.customerName || '—';
      }
      if (col.key === 'vendorName' && row.vendorName) {
        return row.vendorName;
      }
      if (col.key === 'customerName' && row.listSource) {
        return this.timesheetPartyNameOnly(row.customerName);
      }
      if (col.key === 'vendorName' && row.listSource) {
        return this.timesheetPartyNameOnly(row.vendorName);
      }
      const v = row[col.key];
      if (col.bool) return v ? 'Yes' : 'No';
      if (col.dateUS) {
        return this.formatUsDate(v);
      }
      if (v === null || v === undefined || v === '') return '—';
      if (col.key === 'status' && typeof v === 'string') {
        return v
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (ch) => ch.toUpperCase());
      }
      if (
        col.money ||
        col.key === 'balance' ||
        col.key === 'totalAmount' ||
        col.key === 'total' ||
        col.key === 'paidAmount' ||
        col.key === 'balanceAmount' ||
        col.key === 'subtotal' ||
        col.key === 'taxAmount' ||
        col.key === 'bonusAmount' ||
        col.key === 'discountAmount'
      ) {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2) : v;
      }
      return v;
    },
    timesheetPartyNameOnly(name) {
      if (name == null || String(name).trim() === '') return '—';
      return String(name).trim();
    },
    timesheetConsultantIdText(row) {
      const id = row?.consultantId;
      if (id == null || String(id).trim() === '') return '';
      return String(id).trim();
    },
    formatUsDate(value) {
      if (value === null || value === undefined || value === '') return '—';
      const s = String(value);
      const isoDay = s.length >= 10 ? s.slice(0, 10) : s;
      const d = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(isoDay) ? `${isoDay}T12:00:00` : s);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    managerTimesheetListSourceForAttachments(listSource) {
      return listSource != null ? listSource : this.editTimesheetListSource;
    },
    managerTimesheetAttachmentUrl(attachmentId, listSource = null) {
      const src = this.managerTimesheetListSourceForAttachments(listSource);
      const seg = src === 'hr' ? 'hr-timesheet-attachments' : 'timesheet-attachments';
      return `${process.env.VUE_APP_API_URL}/client-manager/clients/${this.clientId}/company/${this.companyId}/${seg}/${attachmentId}`;
    },
    /** POST / DELETE / PATCH …/timesheets|hr-timesheets/:pk/attachments[…] */
    managerTimesheetAttachmentsCollectionUrl(timesheetPk) {
      return this.managerTimesheetAttachmentsCollectionUrlForSheet({
        id: timesheetPk,
        listSource: this.editTimesheetListSource === 'hr' ? 'hr' : 'consultant'
      });
    },
    managerTimesheetAttachmentsCollectionUrlForSheet(sheet) {
      if (!sheet || sheet.id == null) return '';
      const src =
        sheet.listSource === 'hr' ? 'hr' : sheet.listSource === 'consultant' ? 'consultant' : 'consultant';
      const seg = src === 'hr' ? 'hr-timesheets' : 'timesheets';
      return `${this.baseUrl()}/${seg}/${sheet.id}/attachments`;
    },
    resolveTimesheetListSourceFromRow(row) {
      if (!row) return this.tsListSource === 'hr' ? 'hr' : 'consultant';
      if (row.listSource === 'hr') return 'hr';
      if (row.listSource === 'consultant') return 'consultant';
      return this.tsListSource === 'hr' ? 'hr' : 'consultant';
    },
    syncEditRowFromRowsIfOpen(timesheetId) {
      if (!this.editOpen || !this.editRow || timesheetId == null) return;
      if (String(this.editRow.id) !== String(timesheetId)) return;
      const found = this.rows.find((r) => String(r.id) === String(timesheetId));
      if (!found) return;
      const listSrc = this.resolveTimesheetListSourceFromRow(found);
      this.editRow = { ...found, listSource: found.listSource || listSrc };
      this.editTimesheetListSource = listSrc;
    },
    async refreshManagerEditRowAfterAttachmentChange(merge = null) {
      const timesheetId = this.editRow?.id;
      if (!timesheetId) return;
      await this.fetchRows();
      const found = this.rows.find((r) => String(r.id) === String(timesheetId));
      const listSrc = this.editTimesheetListSource === 'hr' ? 'hr' : 'consultant';
      if (found && this.editOpen) {
        this.editRow = { ...found, listSource: found.listSource || listSrc };
        this.editTimesheetListSource = this.resolveTimesheetListSourceFromRow(found);
        return;
      }
      if (!this.editOpen || !merge) return;
      if (merge.type === 'delete' && merge.attachmentId != null) {
        const atts = (this.editRow.attachments || []).filter((a) => String(a.id) !== String(merge.attachmentId));
        this.editRow = { ...this.editRow, attachments: atts, attachmentCount: atts.length };
      } else if (merge.type === 'add' && merge.attachment) {
        const atts = [...(this.editRow.attachments || []), merge.attachment];
        this.editRow = { ...this.editRow, attachments: atts, attachmentCount: atts.length };
      } else if (merge.type === 'rename' && merge.attachment) {
        const atts = (this.editRow.attachments || []).map((a) =>
          String(a.id) === String(merge.attachment.id) ? { ...a, ...merge.attachment } : a
        );
        this.editRow = { ...this.editRow, attachments: atts };
      }
    },
    showTimesheetAttachmentUserMessage(message) {
      if (this.isManagerTimesheetsView && this.editOpen) {
        this.timesheetAttachmentError = message;
        return;
      }
      if (this.isManagerTimesheetsView) {
        this.timesheetCardAttachmentError = message;
        return;
      }
      window.alert(message);
    },
    clearTimesheetAttachmentError() {
      this.timesheetAttachmentError = '';
    },
    clearTimesheetCardAttachmentError() {
      this.timesheetCardAttachmentError = '';
    },
    clearTimesheetAttachmentFeedback() {
      this.timesheetAttachmentSuccess = '';
      if (this.attachmentSuccessTimer) {
        clearTimeout(this.attachmentSuccessTimer);
        this.attachmentSuccessTimer = null;
      }
    },
    scheduleAttachmentSuccessClear() {
      if (this.attachmentSuccessTimer) {
        clearTimeout(this.attachmentSuccessTimer);
      }
      this.attachmentSuccessTimer = setTimeout(() => {
        this.timesheetAttachmentSuccess = '';
        this.attachmentSuccessTimer = null;
      }, 4000);
    },
    flashTimesheetAttachmentSuccessModal(message) {
      this.timesheetAttachmentSuccess = message;
      this.scheduleAttachmentSuccessClear();
    },
    async addManagerTimesheetAttachment({ file }) {
      if (!this.editRow?.id || !file) return;
      this.clearTimesheetAttachmentError();
      this.clearTimesheetCardAttachmentError();
      this.clearTimesheetAttachmentFeedback();
      this.timesheetAttachmentsBusy = true;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(
          this.managerTimesheetAttachmentsCollectionUrlForSheet(this.modalTimesheetSheetRef),
          fd
        );
        await this.refreshManagerEditRowAfterAttachmentChange({ type: 'add', attachment: data?.attachment });
        this.flashTimesheetAttachmentSuccessModal('Attachment added.');
      } catch (e) {
        this.showTimesheetAttachmentUserMessage(e.response?.data?.message || 'Upload failed.');
      } finally {
        this.timesheetAttachmentsBusy = false;
      }
    },
    deleteManagerTimesheetAttachment({ attachment }) {
      const sheet = this.modalTimesheetSheetRef;
      if (!sheet?.id || !attachment?.id) return;
      this.clearTimesheetAttachmentError();
      this.clearTimesheetAttachmentFeedback();
      this.attachmentDeleteContext = { sheet, attachment };
      this.showAttachmentDeleteConfirm = true;
    },
    cancelAttachmentDeleteConfirm() {
      this.showAttachmentDeleteConfirm = false;
      this.attachmentDeleteContext = null;
    },
    async confirmAttachmentDelete() {
      const ctx = this.attachmentDeleteContext;
      this.showAttachmentDeleteConfirm = false;
      this.attachmentDeleteContext = null;
      if (!ctx?.sheet?.id || !ctx?.attachment?.id) return;
      this.clearTimesheetAttachmentError();
      this.clearTimesheetCardAttachmentError();
      this.clearTimesheetAttachmentFeedback();
      const { sheet, attachment } = ctx;
      this.timesheetAttachmentsBusy = true;
      try {
        await axios.delete(`${this.managerTimesheetAttachmentsCollectionUrlForSheet(sheet)}/${attachment.id}`);
        await this.fetchRows();
        this.syncEditRowFromRowsIfOpen(sheet.id);
        this.flashTimesheetAttachmentSuccessModal('Attachment removed.');
      } catch (e) {
        this.showTimesheetAttachmentUserMessage(e.response?.data?.message || 'Delete failed.');
      } finally {
        this.timesheetAttachmentsBusy = false;
      }
    },
    async renameManagerTimesheetAttachment({ attachmentId, fileName }) {
      if (!this.editRow?.id || attachmentId == null) return;
      const name = String(fileName || '').trim();
      if (!name) return;
      this.clearTimesheetAttachmentError();
      this.clearTimesheetCardAttachmentError();
      this.clearTimesheetAttachmentFeedback();
      this.timesheetAttachmentsBusy = true;
      try {
        const { data } = await axios.patch(
          `${this.managerTimesheetAttachmentsCollectionUrlForSheet(this.modalTimesheetSheetRef)}/${attachmentId}`,
          { fileName: name }
        );
        await this.refreshManagerEditRowAfterAttachmentChange({ type: 'rename', attachment: data?.attachment });
        this.flashTimesheetAttachmentSuccessModal('File name updated.');
      } catch (e) {
        this.showTimesheetAttachmentUserMessage(e.response?.data?.message || 'Rename failed.');
      } finally {
        this.timesheetAttachmentsBusy = false;
      }
    },
    handleManagerTsPageChange(page) {
      this.tsCurrentPage = page;
    },
    handleManagerTsItemsPerPageChange(n) {
      this.tsItemsPerPage = n;
      this.tsCurrentPage = 1;
    },
    attachmentCountClickable(row) {
      const n = Number(row?.attachmentCount);
      return Number.isFinite(n) && n > 0;
    },
    onManagerTimesheetCardOpenAttachment(payload) {
      const att = payload?.attachment;
      const sheet = payload?.sheet;
      if (!att?.id || !sheet) return;
      const listSrc = this.resolveTimesheetListSourceFromRow(sheet);
      this.viewManagerTimesheetAttachment(att, listSrc);
    },
    async parseBlobErrorIfJson(blob) {
      if (!blob || blob.size > 65536) return null;
      try {
        const text = await blob.text();
        const j = JSON.parse(text);
        return j?.message || j?.error || null;
      } catch {
        return null;
      }
    },
    async viewManagerTimesheetAttachment(att, listSource = null) {
      if (!att?.id) return;
      try {
        const { data, headers } = await axios.get(this.managerTimesheetAttachmentUrl(att.id, listSource), {
          responseType: 'blob'
        });
        const ct = (headers['content-type'] || '').split(';')[0].trim().toLowerCase();
        if (ct === 'application/json') {
          const msg = await this.parseBlobErrorIfJson(data);
          this.showTimesheetAttachmentUserMessage(msg || 'Failed to open attachment.');
          return;
        }
        const mimeType = headers['content-type'] || att.mimeType || 'application/octet-stream';
        const blob = new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 300);
      } catch (e) {
        const body = e.response?.data;
        if (body instanceof Blob) {
          const msg = await this.parseBlobErrorIfJson(body);
          this.showTimesheetAttachmentUserMessage(msg || 'Failed to open attachment.');
        } else {
          this.showTimesheetAttachmentUserMessage('Failed to open attachment.');
        }
      }
    },
    async downloadManagerTimesheetAttachment(att, listSource = null) {
      if (!att?.id) return;
      try {
        const { data, headers } = await axios.get(this.managerTimesheetAttachmentUrl(att.id, listSource), {
          responseType: 'blob'
        });
        const ct = (headers['content-type'] || '').split(';')[0].trim().toLowerCase();
        if (ct === 'application/json') {
          const msg = await this.parseBlobErrorIfJson(data);
          this.showTimesheetAttachmentUserMessage(msg || 'Failed to download attachment.');
          return;
        }
        const mimeType = headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = att.fileName || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        const body = e.response?.data;
        if (body instanceof Blob) {
          const msg = await this.parseBlobErrorIfJson(body);
          this.showTimesheetAttachmentUserMessage(msg || 'Failed to download attachment.');
        } else {
          this.showTimesheetAttachmentUserMessage('Failed to download attachment.');
        }
      }
    },
    patchEditForm(partial) {
      if (!partial || typeof partial !== 'object') return;
      Object.assign(this.editForm, partial);
    },
    goBack() {
      this.$router.push({
        name: 'ClientManagerDashboard',
        query: { tab: 'clients', focusClient: String(this.clientId) }
      });
    },
    managerRecordsRoute(routeName) {
      const query = {};
      if (this.companyName) query.companyName = this.companyName;
      return {
        name: routeName,
        params: {
          clientId: String(this.clientId),
          companyId: String(this.companyId)
        },
        query
      };
    },
    openEdit(row) {
      const listSrc =
        this.routeName === 'ManagerCompanyTimesheets'
          ? this.resolveTimesheetListSourceFromRow(row)
          : row.listSource === 'hr'
            ? 'hr'
            : 'consultant';
      this.editRow =
        this.routeName === 'ManagerCompanyTimesheets' ? { ...row, listSource: row.listSource || listSrc } : row;
      this.saveError = null;
      this.timesheetAttachmentError = '';
      this.timesheetCardAttachmentError = '';
      this.clearTimesheetAttachmentFeedback();
      this.showAttachmentDeleteConfirm = false;
      this.attachmentDeleteContext = null;
      this.editTimesheetListSource = listSrc;
      const form = {};
      const fieldDefs =
        this.routeName === 'ManagerCompanyTimesheets' ? this.timesheetModalEditFields : this.editFields;
      fieldDefs.forEach((f) => {
        let v = row[f.key];
        if (f.type === 'checkbox') {
          form[f.key] = Boolean(v);
        } else if (v === null || v === undefined) {
          form[f.key] = f.type === 'number' ? 0 : '';
        } else {
          form[f.key] = v;
        }
      });
      if (this.routeName === 'ManagerCompanyTimesheets') {
        form.fromDate = this.normalizeRowDateIso(row.fromDate);
        form.toDate = this.normalizeRowDateIso(row.toDate);
        const th = row.totalHours;
        form.hours =
          th !== null && th !== undefined && th !== '' && Number.isFinite(Number(th))
            ? Number(th)
            : '';
      }
      if (this.routeName === 'ManagerCompanyInvoices') {
        if (form.date !== undefined) form.date = this.normalizeRowDateIso(form.date);
        if (form.dueDate !== undefined) form.dueDate = this.normalizeRowDateIso(form.dueDate);
      }
      this.editForm = form;
      this.editOpen = true;
    },
    closeEdit() {
      this.editOpen = false;
      this.editRow = null;
      this.saveError = null;
      this.timesheetAttachmentError = '';
      this.timesheetCardAttachmentError = '';
      this.clearTimesheetAttachmentFeedback();
      this.showAttachmentDeleteConfirm = false;
      this.attachmentDeleteContext = null;
      this.editTimesheetListSource = 'consultant';
    },
    async saveEdit() {
      if (!this.editRow || !this.config) return;
      this.saving = true;
      this.saveError = null;
      const id = this.editRow[this.config.idKey];
      const payload = {};
      const fieldsForPayload = this.isManagerTimesheetsView ? this.timesheetModalEditFields : this.editFields;
      fieldsForPayload.forEach((f) => {
        const v = this.editForm[f.key];
        if (f.type === 'checkbox') {
          payload[f.key] = Boolean(v);
        } else if (f.alwaysSendInPayload) {
          payload[f.key] = v == null ? '' : String(v);
        } else if (f.type === 'date') {
          const iso = this.normalizeRowDateIso(v);
          if (iso) payload[f.key] = iso;
        } else if (v !== '' && v !== undefined && v !== null) {
          payload[f.key] = f.type === 'number' ? Number(v) : v;
        }
      });
      const periodAllowed =
        this.isManagerTimesheetsView &&
        (this.editTimesheetListSource === 'hr'
          ? this.editForm.status === 'Submitted'
          : this.editForm.status === MANAGER_TIMESHEET_PERIOD_EDITABLE_STATUS);
      if (periodAllowed) {
        const { fromDate, toDate, hours } = this.editForm;
        if (!fromDate || !toDate) {
          this.saveError = 'From date and To date are required.';
          this.saving = false;
          return;
        }
        if (fromDate > toDate) {
          this.saveError = 'From date cannot be after To date.';
          this.saving = false;
          return;
        }
        payload.fromDate = fromDate;
        payload.toDate = toDate;
        if (hours === '' || hours === undefined || hours === null) {
          this.saveError = 'Hours is required.';
          this.saving = false;
          return;
        }
        const h = Number(hours);
        if (!Number.isFinite(h) || h < 0) {
          this.saveError = 'Hours must be a non-negative number.';
          this.saving = false;
          return;
        }
        if (this.editTimesheetListSource === 'hr') {
          payload.totalHours = h;
        } else {
          payload.hours = h;
        }
      }
      const patchSegment =
        this.isManagerTimesheetsView && this.editTimesheetListSource === 'hr'
          ? 'hr-timesheets'
          : this.config.patchSegment;
      try {
        await axios.patch(`${this.baseUrl()}/${patchSegment}/${id}`, payload);
        this.closeEdit();
        await this.fetchRows();
      } catch (e) {
        this.saveError = e.response?.data?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
